import { type SuiteApp, suiteApps } from "../enums";

// Enums of the party domain that travel in the contracts. `@nexxt/db` builds the Postgres enums `user_status`,
// `role_audience` and `org_type` from the first three tuples, the ones of the directory from those further down, and
// `etaf_status` from the last.

/** `invited`: created, never signed in. `blocked`: stopped by an administrator. `disabled`: no longer a user. */
export const userStatuses = ["invited", "active", "blocked", "disabled"] as const;
export type UserStatus = (typeof userStatuses)[number];

/**
 * The app a role opens (ADR-157): Backstage for the users of Tomorrow, Atelier for the ones of brands and suppliers,
 * Frontrow for the ones of customers.
 */
export const roleAudiences = suiteApps;
export type RoleAudience = SuiteApp;

/** `internal` is Tomorrow; every other organization is `external`. */
export const orgTypes = ["internal", "external"] as const;
export type OrgType = (typeof orgTypes)[number];

/** What an organization is to Tomorrow; one can be more than one (a brand that supplies its own goods). */
export const orgKinds = ["brand", "supplier", "customer"] as const;
export type OrgKind = (typeof orgKinds)[number];

/**
 * The business roles Business Central keeps a record of (I-26): a customer, and a supplier as its vendor. `@nexxt/db`
 * builds `bc_sync_kind` from it.
 */
export const bcRecordKinds = ["customer", "supplier"] as const satisfies readonly OrgKind[];
export type BcRecordKind = (typeof bcRecordKinds)[number];

/**
 * Why a record Business Central sent has no organization yet (I-27, ADR-108): nobody has its VAT number or its code,
 * more than one has, the organization it names is not one of that kind, or the organization found already has
 * another record of that kind. `@nexxt/db` builds `bc_unmatched_reason` from it.
 */
export const bcUnmatchedReasons = ["no_match", "ambiguous", "unknown_organization", "already_matched"] as const;
export type BcUnmatchedReason = (typeof bcUnmatchedReasons)[number];

/**
 * The objects of HubSpot the master data of Nexxt comes from in F1 (I-28, ADR-109). `@nexxt/db` builds
 * `hubspot_record_kind` from it.
 */
export const hubspotRecordKinds = ["brand", "contact", "company", "buying_group", "owner"] as const;
export type HubspotRecordKind = (typeof hubspotRecordKinds)[number];

/**
 * Why a record of HubSpot is not in the directory (ADR-109, ADR-110). `not_applied`: nothing was done with it yet.
 * Of a brand: `invalid_type`, HubSpot gives it no type Nexxt knows; `ambiguous`, more than one brand of Nexxt has its
 * name; `already_matched`, the brand found is already another record's. Of an owner: `no_user`, no user of Tomorrow
 * has its email, and `already_matched`. Of a company: `invalid_code`, its customer code is longer than a customer
 * code of Nexxt; `ambiguous`, more than one organization has its VAT number; `already_matched`; `not_customer`, the
 * only organization with its VAT number is not a customer (a supplier, a brand), and a person confirms whether the
 * company is that organization or a new one (ADR-191). Of a contact: `no_organization`, none of the companies or
 * brands it names is in the directory; `invalid_contact`, it has neither a name nor an email. `@nexxt/db` builds
 * `hubspot_waiting_reason` from it.
 */
export const hubspotWaitingReasons = [
  "not_applied",
  "invalid_type",
  "ambiguous",
  "already_matched",
  "no_user",
  "invalid_code",
  "no_organization",
  "invalid_contact",
  "not_customer",
] as const;
export type HubspotWaitingReason = (typeof hubspotWaitingReasons)[number];

/**
 * Where a full reading of HubSpot is (ADR-109, ADR-191): `open` while its parts arrive; `incomplete`, a later reading
 * of the kind began before its parts all arrived; `held`, complete, but it would archive more than the share of
 * `hubspot.full_reading_archive_max_percent`, and waits for a person; `completed`, what it does not name is archived;
 * `discarded`, it archived nothing, by a person or because a later reading of the kind replaced it. `@nexxt/db` builds
 * `hubspot_reading_status` from it.
 */
export const hubspotReadingStatuses = ["open", "incomplete", "held", "completed", "discarded"] as const;
export type HubspotReadingStatus = (typeof hubspotReadingStatuses)[number];

/** What a person does with a reading that waits for one (ADR-191): archive what it does not name, or nothing. */
export const hubspotReadingDecisions = ["release", "discard"] as const;
export type HubspotReadingDecision = (typeof hubspotReadingDecisions)[number];

/**
 * The systems that are the master of fields of the directory while they have the record (ADR-108, ADR-109, ADR-110):
 * a form shows those fields and does not change them (ADR-116).
 */
export const masterSources = ["hubspot", "business_central"] as const;
export type MasterSource = (typeof masterSources)[number];

/**
 * What a contact is to a brand, as HubSpot names it (ADR-110): it buys it, Tomorrow wants it to, or it bought it.
 * `@nexxt/db` builds `brand_interest` from it.
 */
export const brandInterests = ["active", "target", "former"] as const;
export type BrandInterest = (typeof brandInterests)[number];

/** What a user of Tomorrow is to a brand: the crew HubSpot gives it by owner (ADR-109). `@nexxt/db` builds `assignee_role`. */
export const brandAssigneeRoles = [
  "sales_rep",
  "brand_manager",
  "ops_support",
  "ops_support_post_campaign",
  "logistic_support",
] as const;
export type BrandAssigneeRole = (typeof brandAssigneeRoles)[number];

// The tuples below are the values of the directory (organizations, addresses, contacts, customers): `@nexxt/db`
// builds `legal_form`, `address_type`, `contact_role`, `sales_team_score`, `cs_score` and `credit_insurer` from them.

export const legalForms = [
  "limited_company",
  "partnership",
  "plc",
  "srl",
  "srls",
  "spa",
  "individual",
  "other",
] as const;
export type LegalForm = (typeof legalForms)[number];

export const addressTypes = ["registered", "billing", "shipping", "trading_store", "pickup"] as const;

export const contactRoles = [
  "main",
  "accounts_payable",
  "buyer",
  "store_manager",
  "brand_contact",
  "supplier_contact",
  "other",
] as const;
export type ContactRole = (typeof contactRoles)[number];

/**
 * Why the email of a contact does not work (ADR-320): the address bounced for good on a send of Guestlist. A new email
 * clears it. `@nexxt/db` builds `email_invalid_reason` from it.
 */
export const emailInvalidReasons = ["hard_bounce"] as const;
export type EmailInvalidReason = (typeof emailInvalidReasons)[number];

/**
 * Where the erasure of a contact on request stands (ADR-321): its steps under way, stopped at a step for a person to
 * look at, or done. `@nexxt/db` builds `contact_erasure_status` from it.
 */
export const contactErasureStatuses = ["erasing", "stalled", "erased"] as const;
export type ContactErasureStatus = (typeof contactErasureStatuses)[number];

/**
 * The roles of the contacts that hear of the orders of their customer, who order (ADR-158): a contact has one only
 * when Customer Service gives it, and a new one is `other` (ADR-180).
 */
export const orderContactRoles = ["main", "buyer"] as const satisfies readonly ContactRole[];

/** Allocation rule R6. */
export const salesTeamScores = ["best", "good", "standard", "low", "personal", "blocked"] as const;

/** Allocation rule R7. */
export const csScores = ["easy_client", "standard"] as const;

export const creditInsurers = ["none", "hilldun", "other"] as const;

/**
 * Where a customer is in its life (ADR-289): a prospect Sales follows before it orders, or a customer. A prospect
 * becomes a customer with its approved e-TAF, or when Tomorrow says so; never the other way.
 */
export const customerStages = ["prospect", "customer"] as const;
export type CustomerStage = (typeof customerStages)[number];

/** Why two customers are proposed as duplicates of each other. */
export const duplicateMatches = ["vat_number", "name"] as const;
export type DuplicateMatch = (typeof duplicateMatches)[number];

/**
 * Where the e-TAF of a customer is (ADR-105). `draft`: opened, nothing sent yet. `submitted`: the customer sent the
 * form. `amended`: Tomorrow wrote the form, or corrected it. `in_review`: an approval that was under way in the
 * portal Nexxt replaces; Nexxt never writes it. The first three wait for Tomorrow.
 */
export const etafStatuses = ["draft", "submitted", "in_review", "approved", "rejected", "amended"] as const;
export type EtafStatus = (typeof etafStatuses)[number];

/**
 * Where an e-TAF comes from (ADR-180): `nexxt`, opened in Nexxt; `migrated`, the approval a customer had in the portal
 * of today, which the migration writes already approved and nothing in Nexxt writes.
 */
export const etafOrigins = ["nexxt", "migrated"] as const;
export type EtafOrigin = (typeof etafOrigins)[number];
